import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button
} from "@mui/material"
import { criarFamiliar, buscarFamiliarPorEmail } from "./api"
import { recoverPassword } from "../login/api"
import { supabaseAdminClient } from "../../Supabase/supabaseRealtimeClient"
import { sendInvite } from "../../Supabase/supabaseClient"

export interface FamilyMember {
  id: string
  name: string
  relationship: string
  phone: string
  email: string
  isEmergencyContact: boolean
}

interface FamilyMemberDialogProps {
  open: boolean
  onClose: () => void
  initialData?: FamilyMember | null
  onSave: (member: FamilyMember) => void
  isEditing?: boolean
}

const emptyMember: FamilyMemberFormData = {
  name: "",
  relationship: "",
  phone: "",
  email: "",
  isEmergencyContact: false
}

interface FamilyMemberFormData {
  name: string
  relationship: string
  phone: string
  email: string
  isEmergencyContact: boolean
}



const FamilyMemberDialog: React.FC<FamilyMemberDialogProps> = ({
  open,
  onClose,
  initialData,
  onSave,
  isEditing: propIsEditing
}) => {
  const [formData, setFormData] = useState<FamilyMemberFormData>(emptyMember)
  const [loading, setLoading] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [password, setPassword] = useState("")

  const isEditing = propIsEditing ?? !!initialData

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData({
          name: initialData.name,
          relationship: initialData.relationship,
          phone: initialData.phone,
          email: initialData.email,
          isEmergencyContact: initialData.isEmergencyContact
        })
      } else {
        setFormData(emptyMember)
      }
      setPassword("")
      setEmailError(null)
    }
  }, [initialData, open])

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    if (name === "email") {
      setEmailError(validateEmail(value) ? null : "E-mail inválido")
    }
  }

  let isSending = false

  const handleSave = async () => {
    if (isSending) return
    isSending = true

    const trimmedEmail = formData.email.trim()
    const registrationPassword = password;

    try {
      if (
        !formData.name ||
        !formData.relationship ||
        !formData.phone ||
        !trimmedEmail ||
        (!isEditing && !registrationPassword)
      ) {
        alert("Por favor, preencha todos os campos obrigatórios.")
        isSending = false
        return
      }

      if (emailError) {
        alert(emailError)
        isSending = false
        return
      }

      setLoading(true)

      if (isEditing && initialData) {
        // Logica de edição... (simplificada para este exemplo)
        // No mundo real chamaria uma API de update
        onSave({ ...initialData, ...formData, email: trimmedEmail })
      } else {
        // Criar novo
        const familiarExistente = await buscarFamiliarPorEmail(trimmedEmail)
        if (familiarExistente) {
          alert("Este e-mail já está em uso por outro familiar.")
          setLoading(false)
          isSending = false
          return
        }

        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
        const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY
        const serviceRoleKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY
        const clienteId = localStorage.getItem("clienteId")

        if (!supabaseUrl || !serviceKey || !serviceRoleKey || !clienteId) {
          throw new Error("Configuração do Supabase ou clienteId ausente")
        }

        // 1. Criar novo usuário no Auth e no Banco via Edge Function (Atômico)
        console.log("[FamilyMemberDialog] Criando novo usuário e registro via Function")
        const tableData = {
          cliente_id: parseInt(clienteId),
          nome: formData.name,
          parentesco: formData.relationship,
          telefone: formData.phone,
          email: trimmedEmail,
          contato_emergencia: formData.isEmergencyContact
        }
        
        const { user, record } = await sendInvite(trimmedEmail, registrationPassword, 'familiar', tableData)
        if (user && record) {
          alert("Familiar cadastrado com sucesso!")
          onSave(record)
        }
      }
      onClose()
    } catch (error: any) {
      console.error(error)
      alert(`Erro: ${error.message || "Erro inesperado"}`)
    } finally {
      setLoading(false)
      isSending = false
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>
        {isEditing ? "Editar Familiar" : "Adicionar Familiar"}
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin='dense'
          label='Nome Completo'
          name='name'
          fullWidth
          variant='outlined'
          value={formData.name}
          onChange={handleChange}
          required
        />
        <TextField
          margin='dense'
          label='Parentesco'
          name='relationship'
          fullWidth
          variant='outlined'
          value={formData.relationship}
          onChange={handleChange}
          required
        />
        <TextField
          margin='dense'
          label='Telefone'
          name='phone'
          fullWidth
          variant='outlined'
          value={formData.phone}
          onChange={handleChange}
          required
        />
        <TextField
          margin='dense'
          label='Email'
          name='email'
          type='email'
          fullWidth
          variant='outlined'
          value={formData.email}
          onChange={handleChange}
          required
          error={!!emailError}
          helperText={emailError}
        />
        {!isEditing && (
          <TextField
            margin="normal"
            label="Senha"
            type="password"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={loading || !!emailError}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FamilyMemberDialog
